import { useCallback } from "react";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useUpdateFormsStatusForAdmin } from "src/generated/hooks/form-service/queries";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import * as GQL from "src/types/graphql";
import { UPDATE_BULK_FORM_STATUS } from "../graphql/mutations";

export function useFormStatusUpdates() {
  const features = useFlags(["form-service-form-status"]);
  const gqlHook = useFormStatusUpdatesGQL();
  const restHook = useFormStatusUpdatesRest();

  if (features["form-service-form-status"].enabled) {
    return restHook;
  } else {
    return gqlHook;
  }
}

function useFormStatusUpdatesGQL() {
  const [updateBulkFormStatus, { remoteData }] = useRemoteDataMutation<
    GQL.UpdateBulkFormStatus,
    GQL.UpdateBulkFormStatusVariables
  >(UPDATE_BULK_FORM_STATUS);

  const createExtraOptions = (value: GQL.form_status_enum) => {
    if (value === GQL.form_status_enum.Submitted) {
      return {
        submitted_at: "now()",
        deleted_at: null,
        submitted_before: true,
      };
    }

    if (value === GQL.form_status_enum.Deleted) {
      return { deleted_at: "now()", submitted_at: null };
    }

    if (
      value === GQL.form_status_enum.Verified ||
      value === GQL.form_status_enum.LotteryReady
    )
      return { deleted_at: null };

    return { submitted_at: null, deleted_at: null };
  };

  const updateFn = useCallback(
    async (formIds: string[], value: GQL.form_status_enum) => {
      const result = await updateBulkFormStatus({
        variables: {
          form_ids: formIds,
          skipOfferWaitlist: value !== GQL.form_status_enum.Cancelled,
          set: {
            status: value,
            source: null,
            ...createExtraOptions(value),
          },
        },
      });

      try {
        await FSR.createRuleExecutions({
          body: {
            data: {
              targets: formIds.map((id) => ({
                type: "form" as const,
                formId: id,
              })),
            },
          },
        });
      } catch (error) {
        console.warn("Error executing form rules:", error);
      }

      return result;
    },
    [updateBulkFormStatus]
  );

  return { updateFormStatus: updateFn, isLoading: remoteData.isLoading() };
}

function useFormStatusUpdatesRest() {
  const { mutateAsync, isPending } = useUpdateFormsStatusForAdmin();
  const updateFormStatus = useCallback(
    async (formIds: string[], value: GQL.form_status_enum) => {
      return await mutateAsync({
        body: {
          data: {
            formIds,
            status: value,
          },
        },
      });
    },
    [mutateAsync]
  );
  return { updateFormStatus, isLoading: isPending };
}
